import {
  buildActionCardProps,
  type ActionCardAnalyticsContext,
} from '@littlebird/action-cards';
/**
 * Shared message-sending code for typed drafts and transcribed speech.
 *
 * A spoken message goes through the transcript sender the on-screen chat
 * input registered (`registerComposerVoiceSend`) when one is mounted.
 * Otherwise, `sendBackgroundVoiceTranscript` sends without an input. Both
 * routes use `performAttributedSend` to resolve the destination chat.
 */
import {
  connectionCardCompletionMessageId,
  isChatStatusBusy,
  type AskUserAnswers,
  type SendMessageOutcome,
} from '@littlebird/sdk';
import { runInAction } from 'mobx';
import { v4 as uuidv4 } from 'uuid';

import { tabStore } from '@/features/tabs/tabs.store';
import { logError } from '@/lib/errorLogging';
import { littlebirdSdk } from '@/lib/sdk-init';
import { settingsStore } from '@/stores/settings.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { NEW_CHAT_ID } from '@/utils/constants';
import { makeDevEventLog } from '@/utils/dev-event-log';

import { chatEngineStore } from './chat-engine.store';
import { USER_DID_SEND_MESSAGE_EVENT, localChatEventsBus } from './chat.events';
import { resolveChatMode } from './chat.hooks';
import { chatStore } from './chat.store';
import {
  getTabIdFromDraftId,
  isNewChatDraftId,
  resolveNewChatProjectId,
} from './draft-ids';
import { screenContextSearchStore } from './screen-context-search.store';
import { isVoiceDestinationUsable } from './voice/voice-destination';

/** An existing chat disappeared before a pending send resolved its destination */
export class ChatUnavailableError extends Error {}

/** Send one completed voice transcript without reading or changing a typed draft */
type VoiceSend = (transcript: string) => Promise<void>;

/**
 * Each on-screen chat input (`Composer`) registers a transcript sender under
 * its draft ID so Voice Mode can reuse that input's new-chat, project, and
 * context setup without sending the typed draft. Without a sender registered
 * for the session's draft ID, Voice Mode sends in the background.
 *
 * Registrations form a stack so each cleanup removes only its own sender. The
 * latest input registered for the draft ID sends; unmounting it restores the
 * previous sender.
 *
 * The map lives on globalThis: dev hot reload can leave two live copies of
 * this module, and every copy must see the same registrations. Prod loads
 * this module once, so the map behaves like a plain module const there.
 *
 * TODO: Expose composer senders from the SDK's voice mode instead of
 * keeping this module-side registry. The SDK survives hot reload, so that
 * also removes the need for the globalThis workaround.
 */
const composerVoiceSends: Map<string, VoiceSend[]> = ((
  globalThis as { __littlebirdComposerVoiceSends?: Map<string, VoiceSend[]> }
).__littlebirdComposerVoiceSends ??= new Map());

/** Register one chat input's spoken-message sender and return a function that removes it */
export function registerComposerVoiceSend(
  draftId: string,
  send: VoiceSend
): () => void {
  const registrants = composerVoiceSends.get(draftId) ?? [];
  registrants.push(send);
  composerVoiceSends.set(draftId, registrants);

  return () => {
    // A late cleanup must not remove newer registrations under the same draft ID
    if (composerVoiceSends.get(draftId) !== registrants) return;
    const index = registrants.lastIndexOf(send);
    if (index !== -1) registrants.splice(index, 1);
    if (registrants.length === 0) composerVoiceSends.delete(draftId);
  };
}

const logVoiceSendEvent = makeDevEventLog(
  '[voice-send]',
  '__littlebirdVoiceSendLog',
  200
);

/** Send through the latest on-screen chat input, or in the background without one */
export function sendVoiceTranscript(
  draftId: string,
  transcript: string
): Promise<void> {
  const composerSend = composerVoiceSends.get(draftId)?.at(-1);
  if (!composerSend) {
    // Log fallbacks with the registered IDs so a wrongful one is not silent
    logVoiceSendEvent('backgroundFallback', {
      draftId,
      registeredDraftIds: [...composerVoiceSends.keys()],
    });
    return sendBackgroundVoiceTranscript(draftId, transcript);
  }
  return composerSend(transcript);
}

type AttributedSendDispatch =
  | {
      /** Local chat whose first server send started in the background */
      createdThreadId: string;
      wasPreCreatedThread: boolean;
    }
  | {
      createdThreadId: null;
      /** Request for a chat that already exists on the server */
      sendPromise: Promise<SendMessageOutcome | void>;
    };

type AttributedSendExtras = {
  draftId?: string;
  title?: string;
  context?: string;
  projectId?: string | null;
  askUserAnswers?: AskUserAnswers;
  sourceActionCardId?: string;
  followUpActionCardIds?: string[];
  personIds?: string[];
  groupNames?: string[];
};

type AttributedSendParams = {
  /** Temporary or permanent ID of the chat intended to receive the message */
  threadId: string;
  /** Chat mode sent to the SDK after the destination chat is resolved */
  mode: string;
  /** Shared backend agent config; backend selection takes precedence over mode */
  agent?: string;
  /** Whether this send should request and track a spoken assistant reply */
  requestResponseAudio: boolean;
  /** Text to send directly, used for voice transcripts. Undefined sends the saved draft */
  content?: string;
  /** Stable ID for callers that need a synced, idempotent message receipt */
  userMessageId?: string;
  /** Card that originated this send, for telemetry only */
  originatingCardId?: string;
  /** Card context captured when the user initiated this send */
  cardAnalyticsContext?: ActionCardAnalyticsContext;
  /** Runs after meeting preparation, using any replacement chat ID it returned */
  prepareExtras?: (sendThreadId: string) => AttributedSendExtras;
  /** Called after a meeting chat stored only on this device receives its server ID */
  onMeetingThreadPrepared?: (preparedThreadId: string) => void;
};

/** Operations that keep one spoken send attached to the session that started it */
type SpokenReplyTracker = {
  userMessageId: string | undefined;
  retarget(draftId: string): void;
  markPending(threadId: string): void;
  reportFailure(): void;
};

/**
 * Track the spoken reply owed for one attributed send: the preallocated user
 * message ID the reply will answer, bound to the exact Voice Mode session
 * that requested it.
 */
function createSpokenReplyTracker(
  requestResponseAudio: boolean,
  preallocatedUserMessageId?: string
): SpokenReplyTracker {
  const { voiceMode } = littlebirdSdk;
  // Correlate assistant audio with the user message it answers
  const userMessageId = requestResponseAudio
    ? (preallocatedUserMessageId ?? uuidv4())
    : undefined;
  // Capture the session now: meeting-chat preparation below can finish
  // after this session was replaced
  const session = requestResponseAudio ? voiceMode.session : null;

  return {
    userMessageId,
    /** Move this exact session to its resolved destination */
    retarget(draftId: string) {
      if (!session || voiceMode.session !== session) return;
      voiceMode.retargetSession(session.draftId, draftId);
    },
    /** Record the reply this session is waiting for */
    markPending(threadId: string) {
      // A replacement session may reuse the thread ID, so compare the captured session
      if (!userMessageId || voiceMode.session !== session) return;
      voiceMode.markSpokenReplyPending(threadId, userMessageId);
    },
    /** Report a send that failed before the SDK created its local message */
    reportFailure() {
      if (userMessageId && voiceMode.session === session) {
        voiceMode.notifySendFailure(userMessageId);
      }
    },
  };
}

/**
 * Resolve the destination chat for one typed or spoken send and start the
 * normal SDK send. Returns which route ran (`AttributedSendDispatch`): a
 * local chat was created with its first server send continuing in the
 * background, or an existing synced chat's send was started. Meeting chats
 * are prepared before their draft and context are read; New Chat receives
 * its permanent local ID during initialization.
 */
export async function performAttributedSend(
  params: AttributedSendParams
): Promise<AttributedSendDispatch> {
  const { threadId, mode, requestResponseAudio, content } = params;
  const threadsStore = littlebirdSdk.stores.threads;
  const spokenReply = createSpokenReplyTracker(
    requestResponseAudio,
    params.userMessageId
  );
  const userMessageId =
    params.userMessageId ?? spokenReply.userMessageId ?? uuidv4();

  try {
    let sendThread =
      threadId === NEW_CHAT_ID ? undefined : threadsStore.get(threadId);
    // Do not reinterpret a deleted existing chat as a request to create a new one
    if (threadId !== NEW_CHAT_ID && !sendThread) {
      throw new ChatUnavailableError('This chat is no longer available');
    }
    if (
      sendThread?.meetingId &&
      !sendThread.synced &&
      !sendThread.lastMessageId
    ) {
      // Await without re-checking the session: ending Voice Mode must not
      // cancel a transcript already accepted for sending
      sendThread = await threadsStore.prepareMeetingThread(sendThread);
      if (sendThread.id !== threadId) {
        spokenReply.retarget(sendThread.id);
        screenContextSearchStore.move(threadId, sendThread.id);
        params.onMeetingThreadPrepared?.(sendThread.id);
      }
    }

    const sendThreadId = sendThread?.id ?? NEW_CHAT_ID;
    // Meeting preparation may replace the local chat ID. Read draft and context
    // data with the final destination so they follow the message to that chat
    const extras = params.prepareExtras?.(sendThreadId) ?? {};
    const cardRelations = new Map<string, 'follow_up' | 'source'>();
    for (const cardId of extras.followUpActionCardIds ?? []) {
      if (cardId) cardRelations.set(cardId, 'follow_up');
    }
    for (const cardId of [
      extras.sourceActionCardId,
      params.originatingCardId,
    ]) {
      if (cardId && !cardRelations.has(cardId))
        cardRelations.set(cardId, 'source');
    }
    const cardContext = params.cardAnalyticsContext ?? {
      surface: 'chat' as const,
    };
    for (const [cardId, cardRelation] of cardRelations) {
      const card = littlebirdSdk.stores.actionCards.get(cardId);
      trackEvent('ACTION_CARD_CHAT_MESSAGE_SUBMITTED', {
        ...(card?.analytics ?? {}),
        ...(card
          ? buildActionCardProps(card, cardContext)
          : { card_id: cardId, ...cardContext }),
        card_relation: cardRelation,
      });
    }

    // Show the loader before this message is persisted or sent
    localChatEventsBus.emit(USER_DID_SEND_MESSAGE_EVENT, {
      data: { threadId: sendThreadId, messageId: userMessageId },
    });
    trackEvent('MESSAGE_SENT');
    trackEvent('USER_CHAT_EVENT', {
      actionName: 'Message_Sent',
      chatThread: sendThread?.id ?? 'New Chat',
      source: 'Littlebird',
    });

    const responseAudioVoiceId = requestResponseAudio
      ? settingsStore.responseAudioVoiceId
      : undefined;

    if (!sendThread?.synced) {
      // Start the first server send in the background so the local chat can open immediately
      const isPreCreatedThread = !!sendThread && !sendThread.lastMessageId;
      // Inside one MobX action: the thread appears, the typed draft (when one
      // is consumed) is removed, and Voice Mode retargets before
      // `watchSpeechLifecycle` reacts
      const newThread = runInAction(() => {
        const initializedThread = threadsStore.initializeThreadWithMessage({
          mode,
          agent: params.agent,
          useTodos: false,
          title: extras.title,
          context: extras.context,
          projectId: extras.projectId,
          existingThreadId: isPreCreatedThread ? sendThreadId : undefined,
          draftId: extras.draftId,
          content,
          responseAudioVoiceId,
          userMessageId,
          sourceActionCardId: extras.sourceActionCardId,
          followUpActionCardIds: extras.followUpActionCardIds,
          personIds: extras.personIds,
          groupNames: extras.groupNames,
        });
        spokenReply.retarget(initializedThread.id);
        spokenReply.markPending(initializedThread.id);
        return initializedThread;
      });
      // Persist the mode for the created chat; a New Chat send also makes
      // it the next New Chat default
      chatStore.setChatMode(
        mode,
        threadId === NEW_CHAT_ID ? undefined : newThread.id,
        newThread.id,
        userStore.user?.allowed_chat_modes
      );
      // New Chat saved its choice under the composer draft. The move runs
      // before the background send builds its request
      if (threadId === NEW_CHAT_ID && extras.draftId) {
        screenContextSearchStore.move(extras.draftId, newThread.id);
        chatEngineStore.move(extras.draftId, newThread.id);
      }
      return {
        createdThreadId: newThread.id,
        wasPreCreatedThread: isPreCreatedThread,
      };
    }

    const sendPromise = littlebirdSdk.stores.messages.sendMessage({
      threadId: sendThreadId,
      draftId: extras.draftId,
      mode,
      agent: params.agent,
      useTodos: false,
      context: extras.context,
      content,
      responseAudioVoiceId,
      userMessageId,
      askUserAnswers: extras.askUserAnswers,
      followUpActionCardIds: extras.followUpActionCardIds,
      personIds: extras.personIds,
      groupNames: extras.groupNames,
    });
    spokenReply.markPending(sendThreadId);
    // Once the local message exists the SDK reports failures itself, so a
    // rejection here means the message was never created. The promise is also
    // returned below, so the caller observes the rejection as well
    void sendPromise.catch(spokenReply.reportFailure);
    return {
      createdThreadId: null,
      sendPromise,
    };
  } catch (error) {
    spokenReply.reportFailure();
    throw error;
  }
}

type ConnectionCardCompletionAction = 'connect' | 'enable';

interface ConnectionCardCompletionParams {
  action: ConnectionCardCompletionAction;
  cardKey: string;
  displayName: string;
  threadId: string;
}

export function hasConnectionCardCompletion(
  threadId: string,
  cardKey: string
): boolean {
  return littlebirdSdk.stores.messages.hasConnectionCardCompletion(
    threadId,
    cardKey
  );
}

/** Send a visible user turn after a connection-card action succeeds. */
export async function sendConnectionCardCompletion({
  action,
  cardKey,
  displayName,
  threadId,
}: ConnectionCardCompletionParams): Promise<void> {
  const thread = littlebirdSdk.stores.threads.get(threadId);
  if (!thread || !thread.isAvailableForInteraction) {
    throw new ChatUnavailableError('This chat is no longer available');
  }
  if (isChatStatusBusy(thread.status)) {
    throw new Error('Wait for the current response to finish');
  }

  const verb = action === 'enable' ? 'enabled' : 'connected';
  const dispatch = await performAttributedSend({
    threadId,
    mode: resolveChatMode(threadId),
    requestResponseAudio: false,
    content: `I ${verb} ${displayName}. Continue my original request.`,
    userMessageId: connectionCardCompletionMessageId(threadId, cardKey),
  });
  if (dispatch.createdThreadId === null) {
    await dispatch.sendPromise;
  }
}

/**
 * Send a voice transcript when that chat has no on-screen input registered.
 * This still uses the shared send function (`performAttributedSend`). An
 * unresolved New Chat keeps its saved draft and project while sending only the
 * spoken transcript.
 */
async function sendBackgroundVoiceTranscript(
  draftId: string,
  content: string
): Promise<void> {
  // Cue the rare off-screen failure missed by watchSpeechLifecycle
  if (!isVoiceDestinationUsable(draftId)) {
    littlebirdSdk.voiceMode.stopSessionWithHangupCue(draftId);
    throw new Error('Voice session destination no longer exists');
  }

  const { drafts } = littlebirdSdk.stores;
  const isNewChatDraft = isNewChatDraftId(draftId);
  const draft = isNewChatDraft ? drafts.getDraft(draftId) : undefined;
  const newChatProjectId = resolveNewChatProjectId(draftId, draft?.projectId);

  // The same lookup the on-screen composer uses, so a spoken message sends
  // under the same mode whether or not its chat is visible
  const sendThreadId = isNewChatDraft ? NEW_CHAT_ID : draftId;
  const mode = resolveChatMode(sendThreadId);

  try {
    const dispatch = await performAttributedSend({
      threadId: sendThreadId,
      mode,
      requestResponseAudio: true,
      content,
      prepareExtras: isNewChatDraft
        ? () => ({
            draftId,
            projectId: newChatProjectId,
          })
        : undefined,
    });
    if (dispatch.createdThreadId === null) {
      await dispatch.sendPromise;
      return;
    }
    if (!isNewChatDraft) return;

    await drafts.moveDraft(draftId, dispatch.createdThreadId);
    const ownerTabId = getTabIdFromDraftId(draftId);
    const ownerTab = tabStore.tabs.find((tab) => tab.id === ownerTabId);
    // Do not pull the tab back if the user navigated away while the send was pending
    if (ownerTab?.route.path === '/chats') {
      tabStore.updateTabRoute(ownerTab.id, {
        path: `/chats/${dispatch.createdThreadId}`,
      });
    }
  } catch (error) {
    // No screen owns this send, and Voice Mode receives send failures directly
    logError(error, 'Background voice send failed', {
      context: 'Thread',
      metadata: { draftId },
    });
  }
}
